import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth0 } from "@auth0/auth0-react";
import { useState, useEffect, useMemo, useCallback, useContext } from "react";
import {
  getIdeaById,
  updateIdea,
  toggleIdeaStar,
  getIdeaConfig,
  getIdeaFilteredEvidences,
  getSubThemes,
  getEvidencesAggregations,
} from "../api/idea";
import { getUnreadNotificationsCount, listFields } from "../Api";
import { getCalculatedPerspectivesAi } from "./helpers/ideaHelpers";
import { ActiveFilter } from "./useFilters";

import { getThemes } from "../Api";
import { useFields } from "./useFields";

interface IdeaState {
  _id: string;
  title: string;
  description: string;
  state: "active" | "archived" | "generated";
  status: string;
  priority: string;
  domain: string;
  componentId: string;
  ownerId: string;
  ownerName: string;
  tags: string[];
  score: number;
  createdAt: Date;
  updatedAt: Date;
  starred: string[];
  productItem?: {
    _id: string;
    name: string;
    type: string;
  };
  componentObj?: {
    _id: string;
    name: string;
  };
  duplicateIdea?: {
    _id: string;
    title: string;
    score: number;
  };
  duplicationType?: string;
  duplicationId?: string;
  /** Set when this idea is a child of another; used to hide "Create idea from theme" on child ideas */
  parentIdeaId?: string;
}

interface EvidenceFilters {
  state?: string;
  submitterId?: string;
  domain?: string;
  priority?: string;
}

interface MatchFilters {
  confirmed?: boolean;
  dismissed?: boolean;
  scoreThreshold?: number;
}

interface TabCondition {
  field: string;
  $eq?: any;
  $isNull?: number;
}

interface TabSection {
  index: number;
  label: string;
  conditions: TabCondition[];
}

interface Tab {
  name: string;
  label: string;
  index: number;
  sections: TabSection[];
  showMoreButton?: boolean;
}

export const useIdea = (ideaId?: string, activeFilters?: ActiveFilter[]) => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();
  const { perspectiveConfigurations } = useFields();
  const [activeTab, setActiveTab] = useState<string | null>(null);
  
  const [unreadNotificationsMap, setUnreadNotificationsMap] = useState<any[]>(
    []
  );

  const [comments, setComments] = useState<any[]>([]);
  const [selectedEvidenceIds, setSelectedEvidenceIds] = useState<any[]>([]);

  const themeEvidenceIds = queryClient.getQueryData(['theme-evidence-ids', ideaId]) as any[] | null;
  const setThemeEvidenceIds = useCallback((evidenceIds: any[]) => {
    queryClient.setQueryData(['theme-evidence-ids', ideaId], evidenceIds);
  }, [queryClient, ideaId]);
 
  const [activeSearch, setActiveSearch] = useState(false);
  const [query, setQuery] = useState('')
  const [textQuery, setTextQuery] = useState('')
  const [triggerSearch, setTriggerSearch] = useState(false);
  const [themeLoading, setThemeLoading] = useState(false);
  // New state for search and sorting
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>('');
  const [sortConfig, setSortConfig] = useState<{
    field?: string;
    direction?: 'asc' | 'desc';
  }>({});

  const [unreadNotificationsLoading, setUnreadNotificationsLoading] = useState<boolean>(false);

  // Debounce search query to prevent excessive API calls
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500); // 500ms delay

    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  // Additional state for compatibility with existing context
  const [activityOpened, setActivityOpened] = useState<boolean>(false);
  const [chosenEvidence, setChosenEvidence] = useState<any>(null);
  
  // Perspective data state
  const [perspectiveData, setPerspectiveData] = useState<{ [key: string]: any }>({});
  const [perspectiveDataLoading, setPerspectiveDataLoading] = useState<boolean>(false);

  // Add evidence form screen state
  const [addEvidenceFormScreen, setAddEvidenceFormScreen] = useState<boolean>(false);
  
  // Fetch tabs using the same API as SyncContext
  const {
    data: tabsData,
    isLoading: tabsLoading,
    error: tabsError,
    refetch: refetchTabs,
  } = useQuery({
    queryKey: ['tabs'],
    queryFn: async () => {
      const response = await listFields(auth0);
      return response.tabs || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });

  // Use tabs from context if available, otherwise use fetched tabs
  const currentTabs = tabsData || [];
  
  // Set initial active tab when tabs are loaded
  useEffect(() => {
    if (currentTabs?.length > 0 && !activeTab) {
      setActiveTab(currentTabs[0]?.label || null);
    }
  }, [currentTabs]);
  
  // Fetch single idea
  const {
    data: idea,
    isLoading: loading,
    error: ideaError,
    refetch: refetchIdea,
  } = useQuery({
    queryKey: ['idea', ideaId],
    queryFn: () => getIdeaById(ideaId || '', auth0),
    enabled: !!ideaId,
    placeholderData: () => {
      // Try to find idea in any list cache for instant navigation
      // Search through all ideas list caches
      const listCaches = queryClient.getQueriesData({
        queryKey: ['ideas', 'list']
      });
      
      for (const [, data] of listCaches) {
        const ideas = (data as any)?.ideas || [];
        const foundIdea = ideas.find((idea: any) => idea._id === ideaId);
        if (foundIdea) return foundIdea;
      }
      return undefined;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });

  // Fetch evidences
  const {
    data: evidencesData,
    isLoading: evidencesLoading,
    error: evidencesQueryError,
    refetch: refetchEvidences,
  } = useQuery({
    queryKey: ['idea-evidences', ideaId],
    queryFn: () => getIdeaEvidences(ideaId || '', auth0),
    enabled: !!ideaId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });

  // Fetch matches
  const {
    data: matchesData,
    isLoading: matchesLoading,
    error: matchesQueryError,
    refetch: refetchMatches,
  } = useQuery({
    queryKey: ['idea-matches', ideaId],
    queryFn: () => getIdeaMatches(ideaId || '', auth0),
    enabled: !!ideaId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });

  // Create a stable cache key for the filtered evidences query (excludes search for no-cache behavior)
  const filteredEvidencesCacheKey = useMemo(() => {
    // Sort activeFilters by name to ensure consistent ordering for cache key
    const sortedFilters = activeFilters ? [...activeFilters].sort((a, b) => 
      (a.name || '').localeCompare(b.name || '')
    ).map(filter => ({
      // Only include essential properties for cache key
      name: filter.name,
      selected: filter.selected?.sort(), // Sort selected values for consistency
      operator: filter.operator
    })) : [];
    
    // Create a stable sort key
    const sortKey = sortConfig.field ? `${sortConfig.field}-${sortConfig.direction || 'asc'}` : null;
    
    // Create a hash-like key for better performance with large filter sets
    const filtersHash = sortedFilters.length > 0 ? 
      btoa(JSON.stringify(sortedFilters)).substring(0, 16) : null;
    
    // For search queries, add timestamp to force new API calls every time (debugging phase)
    const searchTimestamp = debouncedSearchQuery ? Date.now() : null;
    
    return [
      'idea-filtered-matches',
      ideaId,
      filtersHash,
      sortKey,
      searchTimestamp
    ].filter(Boolean); // Remove null/undefined values
  }, [ideaId, activeFilters, sortConfig, debouncedSearchQuery]);

  const {
    data: filteredEvidencesData,
    isLoading: filteredEvidencesLoading,
    error: filteredEvidencesQueryError,
    refetch: filterMatches,
  } = useQuery({
    queryKey: filteredEvidencesCacheKey,
    queryFn: async () => {

      const result = await getIdeaFilteredEvidences(
        activeFilters || [], 
        ideaId || '', 
        auth0, 
        debouncedSearchQuery || undefined,
        sortConfig.field ? sortConfig : undefined
      );
      
      /* console.log('✅ Filtered evidences fetched:', result?.evidences?.length || 0, 'evidences'); */
      return result;
    },
    enabled: !!ideaId, // Always enable when we have an ideaId - the API will handle empty filters
    staleTime: 10 * 60 * 1000, // 10 minutes for filters/sort, search uses timestamp cache-busting
    gcTime: 30 * 60 * 1000, // 30 minutes cache retention
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });

  // Use React Query to track selectedTheme for re-renders
  const {
    data: selectedThemeData,
  } = useQuery({
    queryKey: ['selected-theme', ideaId],
    queryFn: () => null, // This query doesn't fetch data, it just tracks the cache
    enabled: !!ideaId,
    staleTime: Infinity, // Never stale since we manage it manually
    gcTime: Infinity, // Never garbage collected
  });

  // Use React Query to manage selectedTheme globally
  const selectedTheme = selectedThemeData as any;
  const setSelectedTheme = useCallback((theme: any) => {
    queryClient.setQueryData(['selected-theme', ideaId], theme);
  }, [queryClient, ideaId]);

 
  // Update idea mutation
  const updateIdeaMutation = useMutation({
    mutationFn: (updatedIdea: Partial<IdeaState>) => {
      if (!idea) throw new Error("No idea to update");
      const ideaToUpdate = { ...idea, ...updatedIdea };
      return updateIdea(ideaToUpdate, auth0);
    },
    onSuccess: (result, updatedIdea) => {
      if (result.success) {
        // Update the cached idea data optimistically - no need to refetch
        queryClient.setQueryData(['idea', ideaId], (oldData: IdeaState) => ({
          ...oldData,
          ...updatedIdea,
          updatedAt: new Date(), // Update timestamp to reflect the change
        }));
        
        // Also invalidate related queries without refetching immediately
        queryClient.invalidateQueries({ 
          queryKey: ['ideas'], 
          refetchType: 'none' // Don't refetch immediately, just mark as stale
        });
      }
    },
    onError: (error) => {
      console.error("Error updating idea:", error);
    },
  });

  // Toggle star mutation
  const toggleStarMutation = useMutation({
    mutationFn: (starred: boolean) => {
      if (!idea) throw new Error("No idea to star");
      return toggleIdeaStar(idea._id, starred, auth0);
    },
    onSuccess: (result) => {
      if (result.success) {
        // Update the cached idea data optimistically
        queryClient.setQueryData(['idea', ideaId], (oldData: IdeaState) => ({
          ...oldData,
          starred: [result.starred],
        }));
        
        // Mark ideas list as stale without immediate refetch
        queryClient.invalidateQueries({ 
          queryKey: ['ideas'], 
          refetchType: 'none'
        });
      }
    },
    onError: (error) => {
      console.error("Error toggling star:", error);
    },
  });

  // Custom fetch functions for filtering
  const fetchEvidences = async (filters?: EvidenceFilters) => {
    if (!ideaId) return;
    
    try {
      const result = await getIdeaEvidences(ideaId, auth0, filters);
      // Update the cache with filtered results
      queryClient.setQueryData(['idea-evidences', ideaId], result);
    } catch (error) {
      console.error("Error fetching evidences:", error);
    }
  };

  const fetchMatches = async (filters?: MatchFilters) => {
    if (!ideaId) return;
    
    try {
      const result = await getIdeaMatches(ideaId, auth0);
      // Update the cache with filtered results
      queryClient.setQueryData(['idea-matches', ideaId], result);
    } catch (error) {
      console.error("Error fetching matches:", error);
    }
  };

  const fetchConfig = async () => {
    try {
      const result = await getIdeaConfig(auth0);
      // Update the cache with config results
      queryClient.setQueryData(['idea-config'], result);
    } catch (error) {
      console.error("Error fetching config:", error);
    }
  };

  // Refresh all data - only when explicitly needed
  const refreshData = async () => {
    if (!ideaId) return;
    
    // Use invalidateQueries instead of direct refetch to respect staleTime
    queryClient.invalidateQueries({ 
      queryKey: ['idea', ideaId],
      refetchType: 'active'
    });
    queryClient.invalidateQueries({ 
      queryKey: ['idea', 'detail', ideaId],
      refetchType: 'active'
    });
    queryClient.invalidateQueries({ 
      queryKey: ['idea-evidences', ideaId],
      refetchType: 'active'
    });
    queryClient.invalidateQueries({ 
      queryKey: ['idea-matches', ideaId],
      refetchType: 'active'
    });
    // Also invalidate any filtered evidences queries that might be cached
    queryClient.invalidateQueries({ 
      queryKey: ['idea', 'detail', ideaId, 'filtered-evidences'],
      refetchType: 'active'
    });
  };

  // Helper function to update idea data
  const updateIdeaData = async (updatedIdea: Partial<IdeaState>) => {
    try {
      await updateIdeaMutation.mutateAsync(updatedIdea);
      return true;
    } catch (error) {
      return false;
    }
  };

  // Helper function to toggle star
  const toggleStar = async (starred: boolean) => {
    try {
      await toggleStarMutation.mutateAsync(starred);
      return true;
    } catch (error) {
      return false;
    }
  };

  const getUnreadNotifications = async () => {
    if (!ideaId) {
      return false;
    }
    setUnreadNotificationsLoading(true);
    await getUnreadNotificationsCount(ideaId, auth0)
      .then(({ requests }) => {
        setUnreadNotificationsMap(requests);
      })
      .catch((e) => console.log(e))
      .finally(() => setUnreadNotificationsLoading(false));
  };

  
  // Extract data from query responses - memoized to prevent unnecessary re-renders
  const evidences = useMemo(() => evidencesData?.evidences || [], [evidencesData?.evidences]);
  const totalEvidencesCount = evidencesData?.totalCount || 0;
  const matches = useMemo(
    () =>
      ((matchesData as any)?.evidences || [])?.map((match: any) => ({
        ...match,
        status: 'match'
      })) || [],
    [matchesData]
  );
  const totalMatchesCount = (matchesData as any)?.totalCount || matches?.length;
  
  // Filtered matches from filtered evidences data
  const filteredMatches = useMemo(() => {
    // If we have themeEvidenceIds, filter based on them
    if (themeEvidenceIds && themeEvidenceIds.length > 0) {
      if (filteredEvidencesData) {
        // Filter from filtered data if available
        const themeEvidences = filteredEvidencesData?.suggestedEvidences?.filter((evidence: any) => 
          themeEvidenceIds.includes(evidence._id)
        ) || [];
        /* console.log("Filtered theme matches from filtered data:", themeEvidences); */
        return themeEvidences;
      } else {
        // Filter from regular matches
        const themeMatches = matches.filter((match: any) => 
          themeEvidenceIds.includes(match._id)
        );
        /* console.log("Filtered theme matches from regular matches:", themeMatches); */
        return themeMatches;
      }
    }
    
    // If no themeEvidenceIds, return appropriate data
    if (filteredEvidencesData) {
      return filteredEvidencesData?.suggestedEvidences || [];
    }
    return matches;
  }, [filteredEvidencesData, matches, themeEvidenceIds]);

  const filteredEvidences = useMemo(() => {
    // If we have themeEvidenceIds, filter based on them
    if (themeEvidenceIds && themeEvidenceIds.length > 0) {
      if (filteredEvidencesData) {
        // Filter from filtered data if available
        const themeEvidences = filteredEvidencesData?.evidences?.filter((evidence: any) => 
          themeEvidenceIds.includes(evidence._id)
        ) || [];
        return themeEvidences;
      } else {
        // Filter from regular evidences
        const themeEvidences = evidences.filter((evidence: any) => 
          themeEvidenceIds.includes(evidence._id)
        );
        return themeEvidences;
      }
    }
    
    // If no themeEvidenceIds, return appropriate data
    if (filteredEvidencesData) {
      return filteredEvidencesData?.evidences || [];
    }
    return evidences;
  }, [filteredEvidencesData, evidences, themeEvidenceIds]);

  const isGenerated = idea?.state === 'generated';
  // Extract searched evidences from filtered evidences data
  const searchedEvidences2 = useMemo(() => {
    /* console.log('🔍 searchedEvidences useMemo called');
    console.log('filteredEvidencesData:', filteredEvidencesData);
    console.log('debouncedSearchQuery:', debouncedSearchQuery);
    console.log('searchQuery:', searchQuery); */
    
    if (filteredEvidencesData) {
      const searchResults = filteredEvidencesData?.searchedEvidences || [];
      /* console.log('🔍 searchedEvidences found:', searchResults.length, searchResults); */
      return searchResults;
    }
    /* console.log('🔍 No filteredEvidencesData - returning empty array'); */
    return [];
  }, [filteredEvidencesData, debouncedSearchQuery, searchQuery]);

  // Extract the three separate arrays from filtered evidences data
  const searchedEvidences = useMemo(() => {
    if (filteredEvidencesData) {
      return filteredEvidencesData?.evidences || [];
    }
    return [];
  }, [filteredEvidencesData]);

  const searchedSuggestedEvidences = useMemo(() => {
    /* console.log('🔍 searchedSuggestedEvidences useMemo called')
    console.log('filteredEvidencesData:', filteredEvidencesData) */
    if (filteredEvidencesData) {
      const result = filteredEvidencesData?.suggestedEvidences || [];
      /* console.log('🔍 searchedSuggestedEvidences result:', result) */
      return result;
    }
    /* console.log('🔍 No filteredEvidencesData - returning empty array') */
    return [];
  }, [filteredEvidencesData]);

  const searchedAdditionalEvidences = useMemo(() => {
    if (filteredEvidencesData) {
      return filteredEvidencesData?.additionalEvidences || [];
    }
    return [];
  }, [filteredEvidencesData]);

  /* console.log('test search results')
  console.log('searchedSuggestedEvidences')
  console.log(searchedSuggestedEvidences)
  console.log('searchedEvidences')
  console.log(searchedEvidences)
  console.log('searchedAdditionalEvidences')
  console.log(searchedAdditionalEvidences) */
  /* console.log(filteredEvidencesData) */
  /* console.log(searchAdditionalEvidences) */

  // Concatenated array of all evidences - memoized to prevent unnecessary re-renders
  const allEvidences = useMemo(() => {
 
    
    // If we have themeEvidenceIds, filter based on them
    if (themeEvidenceIds && themeEvidenceIds.length > 0) {
      if (filteredEvidencesData) {
        // Filter from filtered data if available
        const filteredData = [...filteredEvidencesData?.evidences || [], ...filteredEvidencesData?.suggestedEvidences || []];
        const themeEvidences = filteredData.filter((evidence: any) => themeEvidenceIds.includes(evidence._id));
        return themeEvidences;
      } else {
        // Filter from regular evidences and matches
        const combinedEvidences = [...evidences, ...matches];
        const themeEvidences = combinedEvidences.filter((evidence: any) => themeEvidenceIds.includes(evidence._id));
        return themeEvidences;
      }
    }
    
    // If no themeEvidenceIds, return appropriate data
    if (filteredEvidencesData) {
      const filteredData = [...filteredEvidencesData?.evidences || [], ...filteredEvidencesData?.suggestedEvidences || []];
      return filteredData;
    }
    /* console.log("sssss evi", evidences);
    console.log("sssss mat", matches); */

    // Otherwise use the regular evidences and matches
    const combinedEvidences = [...evidences, ...matches];
    return combinedEvidences;
  }, [filteredEvidencesData, evidences, matches, themeEvidenceIds]);
  // Filter evidences based on tab conditions
  const filterEvidencesByConditions = (evidences: any[], conditions: any[]) => {
    return evidences?.filter(evidence => {
      return conditions?.every(condition => {
        const { field, $eq, $isNull } = condition;
        
        if ($eq !== undefined) {
          return evidence[field] === $eq;
        }
        
        if ($isNull !== undefined) {
          if ($isNull === 0) {
            return evidence[field] != null && evidence[field] !== undefined;
          } else if ($isNull === 1) {
            return evidence[field] == null || evidence[field] === undefined;
          }
        }
        
        return true;
      });
    });
  };


  
  // Create filtered evidences for each tab section
  const getFilteredEvidencesForTab = (tab: Tab) => {
    if (!tab?.sections) return [];
    
    const filteredSections = tab?.sections?.map((section: TabSection) => ({
      ...section,
      evidences: filterEvidencesByConditions(allEvidences, section?.conditions || [])
    }));
    
    return filteredSections;
  };

  const searchEvidence = useCallback((text: string) => {
    console.log('🔍 searchEvidence called with:', text);
    setQuery(text);
    setSearchQuery(text);

    if (text === "") {
      console.log('🔍 Setting activeSearch to false');
      setActiveSearch(false);
    } else {
      console.log('🔍 Setting activeSearch to true');
      setActiveSearch(true);
      setTriggerSearch(!triggerSearch);
    }
  }, [triggerSearch]);

  // New function to handle sorting
  const sortEvidence = useCallback((field: string, direction?: 'asc' | 'desc') => {
    console.log('🔍 sortEvidence called with:', { field, direction });
    console.log('Current sortConfig:', sortConfig);

    const newDirection = direction || (sortConfig.field === field && sortConfig.direction === 'asc' ? 'desc' : 'asc');
    console.log('New sortConfig will be:', { field, direction: newDirection });
    
    setSortConfig({ field, direction: newDirection });
    
    // Trigger the API call after setting sort config
    setTimeout(() => {
      console.log('🔍 Calling filterMatches after sort config update');
      filterMatches();
    }, 0);
  }, [sortConfig, filterMatches]);

  // Function to clear search
  const clearSearch = useCallback(() => {
    setSearchQuery('');
    setDebouncedSearchQuery('');
    setQuery('');
    setActiveSearch(false);
  }, []);

  // Function to clear sort
  const clearSort = useCallback(() => {
    setSortConfig({});
  }, []);

  // Helper function to get current cache key for debugging
  const getCurrentCacheKey = useCallback(() => {
    return filteredEvidencesCacheKey;
  }, [filteredEvidencesCacheKey]);

  // Helper function to check if data is cached
  const isCached = useCallback(() => {
    const cachedData = queryClient.getQueryData(filteredEvidencesCacheKey);
    return !!cachedData;
  }, [queryClient, filteredEvidencesCacheKey]);

  // Helper function to manually invalidate cache
  const invalidateFilteredCache = useCallback(() => {
    queryClient.invalidateQueries({ 
      queryKey: ['idea-filtered-matches', ideaId],
      exact: false // This will invalidate all queries that start with this key
    });
  }, [queryClient, ideaId]);

  // Function to convert activeFilters array to a single string
  const convertActiveFiltersToString = useCallback((filters: ActiveFilter[]): string => {
    if (!filters || filters.length === 0) {
      return '';
    }

    let resultString = '';

    filters.forEach((doc, index) => {
      let filterString = '';

      const filterType = doc.type as string;

      if (filterType === 'checkbox') {
        if (doc.selected && Array.isArray(doc.selected)) {
          filterString = `${doc.name}+${doc.selected.join(',')}`;
        }
      } else if (filterType === 'date') {
        if (doc.selected && Array.isArray(doc.selected) && doc.selected.length === 2) {
          const startDate = new Date(doc.selected[0]).toISOString().split('T')[0];
          const endDate = new Date(doc.selected[1]).toISOString().split('T')[0];
          filterString = `${doc.name}+${startDate}_${endDate}`;
        }
      } else if (filterType === 'fetchableCheckbox') {
        if (doc.selected && Array.isArray(doc.selected)) {
          filterString = `${doc.name}+${doc.selected.join(',')}`;
        }
      } else if (filterType === 'boolean' || filterType === 'userIdIn') {
        if (doc.selected !== undefined && doc.selected !== null) {
          filterString = `${doc.name}+${doc.operator}`;
        }
      } else {
        // Handle unknown types or fallback
        if (doc.selected) {
          if (Array.isArray(doc.selected)) {
            filterString = `${doc.name}+${doc.selected.join(',')}`;
          } else {
            filterString = `${doc.name}+${doc.selected}`;
          }
        }
      }

      // Add to result string with separator
      if (filterString) {
        if (resultString) {
          resultString += '|';
        }
        resultString += filterString;
      }
    });

    return resultString;
  }, []);

  // Get filtered evidences for current active tab
  const currentTabData = useMemo(() =>
    (currentTabs as Tab[]).find((tab: Tab) => tab.label === activeTab),
  [currentTabs, activeTab]
  );

  const filteredEvidencesForActiveTab = useMemo(() =>
    currentTabData ? getFilteredEvidencesForTab(currentTabData) : [],
  [currentTabData, allEvidences, themeEvidenceIds]
  );

  // Initialize context-like values for components that need them
  
  
  // Context-like functions
  const setIsCommentPresent = (comments: any[]) => {
    setComments(comments);
  };

  const loadEvidence = () => {
    // Only refetch if data is stale (respects staleTime)
    queryClient.invalidateQueries({ 
      queryKey: ['idea-evidences', ideaId],
      refetchType: 'active' // Only refetch if query is currently active
    });
    queryClient.invalidateQueries({ 
      queryKey: ['idea-matches', ideaId],
      refetchType: 'active'
    });
    getUnreadNotifications();
  };

  const getUnreadNotificationsCountByRequest = (requestId: string) => {
    return (
      unreadNotificationsMap.find((request) => request._id == requestId)
        ?.unreadNotificationsCount || 0
    );
  };

  // Additional functions for compatibility with existing context
  const isCommentPresent = comments?.length > 0;

  // Calculate perspective data when evidences and matches change
  useEffect(() => {
    if (!perspectiveConfigurations || perspectiveConfigurations.length === 0) {
      return;
    }

    setPerspectiveDataLoading(true);
    const allMatches = [...filteredEvidences, ...filteredMatches];
    getCalculatedPerspectivesAi(allMatches, perspectiveConfigurations).then((data) => {
      setPerspectiveData((prev) => ({
        ...prev,
        ...data
      }));
    })
      .catch((error: any) => {
        console.error("Error recalculating perspective data:", error);
      })
      .finally(() => {
        setPerspectiveDataLoading(false);
      });  
  }, [filteredEvidences, filteredMatches, perspectiveConfigurations, themeEvidenceIds]);


  // Convert query errors to string format
  const error = ideaError ? (ideaError as Error).message : null;
  const evidencesError = evidencesQueryError ? (evidencesQueryError as Error).message : null;
  const matchesError = matchesQueryError ? (matchesQueryError as Error).message : null;
  const tabsErrorString = tabsError ? (tabsError as Error).message : null;

  // Debug useEffect to track themeEvidenceIds changes
  useEffect(() => {
    /* console.log("themeEvidenceIds changed to:", themeEvidenceIds);
    console.log("allEvidences length after themeEvidenceIds change:", allEvidences.length); */
  }, [themeEvidenceIds, allEvidences]);

  // Debug useEffect to track cache key changes
  useEffect(() => {
    /* console.log("🔑 Cache key changed:", filteredEvidencesCacheKey);
    console.log("💾 Is cached:", queryClient.getQueryData(filteredEvidencesCacheKey) ? 'YES' : 'NO'); */
  }, [filteredEvidencesCacheKey, queryClient]);

 
  return {
    // State
    idea: idea || null,
    evidences :  evidences,
    matches: matches,
    allEvidences : allEvidences,
    filteredEvidencesForActiveTab : filteredEvidencesForActiveTab,
    tabs: currentTabs,
    activeTab,
    loading,
    evidencesLoading,
    matchesLoading,
    tabsLoading,
    error,
    evidencesError,
    matchesError,
    tabsError: tabsErrorString,
    totalEvidencesCount,
    totalMatchesCount,
    
    // Search and sort state
    searchQuery,
    debouncedSearchQuery,
    sortConfig,
    activeSearch,
    query,
    
    // Perspective data
    perspectiveData,
    perspectiveDataLoading,
    
    // Context-like state
    comments,
    selectedEvidenceIds,
    isCommentPresent,
    activityOpened,
    chosenEvidence,
    addEvidenceFormScreen,
    setAddEvidenceFormScreen,
    setActiveSearch,
    textQuery,
    setTextQuery,
    isGenerated,
    
    // Actions
    fetchIdea: refetchIdea,
    updateIdeaData,
    toggleStar,
    fetchEvidences,
    fetchMatches,
    fetchTabs: refetchTabs,
    setActiveTab,
    refreshData,
    fetchConfig,
    // Context-like actions
    setIsCommentPresent,
    loadEvidence,
    getUnreadNotificationsCountByRequest,
    setSelectedEvidenceIds,
    setActivityOpened,
    setChosenEvidence,
    
    // Search and sort actions
    searchEvidence,
    sortEvidence,
    clearSearch,
    clearSort,
    setSearchQuery,
    setSortConfig,
    
    // Cache management functions
    getCurrentCacheKey,
    isCached,
    invalidateFilteredCache,
    convertActiveFiltersToString,
    
    // Computed values
    isStarred: idea?.starred?.length ? idea?.starred?.length > 0 : false,
    hasEvidences: evidences?.length > 0,
    hasMatches: matches?.length > 0,
    hasTabs: currentTabs?.length > 0,
    
    // Mutation states
    isUpdating: updateIdeaMutation?.isPending,
    isTogglingstar: toggleStarMutation?.isPending,
    filterMatches,
    
    // Filtered evidences data
    /* filteredEvidences: filteredEvidencesData?.matches || [], */
    filteredEvidencesCount: filteredEvidencesData?.totalCount || 0,
    isFiltering: filteredEvidencesLoading,
    filteringError: filteredEvidencesQueryError,
    filteredMatches, 
    filteredEvidences,
    searchedEvidences,
    searchedSuggestedEvidences,
    searchedAdditionalEvidences,
    setThemeEvidenceIds: setThemeEvidenceIds,
    themeLoading,
    setThemeLoading,
    themeEvidenceIds,
    selectedTheme,
    setSelectedTheme
  };
}; 
